import type { Timesheet, TimesheetApproval, TimesheetEntry } from '@/libs/interfaces'

import { MOCK_EMPLOYEE_ID, MOCK_ORG_ID, MOCK_STORE_ID_1, MOCK_STORE_ID_2 } from './seed'

/**
 * Timesheet fixtures cross-referencing seed.ts employees/stores. Six
 * timesheets spread across three employees, each with a couple of entries
 * and one approval record.
 */

export const mockTimesheets: Timesheet[] = [
  {
    timesheetID: 'timesheet-1',
    orgID: MOCK_ORG_ID,
    employeeID: MOCK_EMPLOYEE_ID,
    storeID: MOCK_STORE_ID_1,
    periodStart: '2025-06-02',
    periodEnd: '2025-06-08',
    status: 'Active',
    submittedBy: MOCK_EMPLOYEE_ID,
    createdAt: '2025-06-09T08:00:00Z',
    updatedAt: '2025-06-09T08:00:00Z',
  },
  {
    timesheetID: 'timesheet-2',
    orgID: MOCK_ORG_ID,
    employeeID: MOCK_EMPLOYEE_ID,
    storeID: MOCK_STORE_ID_1,
    periodStart: '2025-06-09',
    periodEnd: '2025-06-15',
    status: 'Draft',
    submittedBy: MOCK_EMPLOYEE_ID,
    createdAt: '2025-06-16T08:00:00Z',
    updatedAt: '2025-06-16T08:00:00Z',
  },
  {
    timesheetID: 'timesheet-3',
    orgID: MOCK_ORG_ID,
    employeeID: 'employee-2',
    storeID: MOCK_STORE_ID_1,
    periodStart: '2025-06-02',
    periodEnd: '2025-06-08',
    status: 'Active',
    submittedBy: 'employee-2',
    createdAt: '2025-06-09T08:00:00Z',
    updatedAt: '2025-06-09T08:00:00Z',
  },
  {
    timesheetID: 'timesheet-4',
    orgID: MOCK_ORG_ID,
    employeeID: 'employee-2',
    storeID: MOCK_STORE_ID_1,
    periodStart: '2025-06-09',
    periodEnd: '2025-06-15',
    status: 'Terminated',
    submittedBy: 'employee-2',
    createdAt: '2025-06-16T08:00:00Z',
    updatedAt: '2025-06-16T08:00:00Z',
  },
  {
    timesheetID: 'timesheet-5',
    orgID: MOCK_ORG_ID,
    employeeID: 'employee-3',
    storeID: MOCK_STORE_ID_2,
    periodStart: '2025-06-02',
    periodEnd: '2025-06-08',
    status: 'Active',
    submittedBy: 'employee-3',
    createdAt: '2025-06-09T08:00:00Z',
    updatedAt: '2025-06-09T08:00:00Z',
  },
  {
    timesheetID: 'timesheet-6',
    orgID: MOCK_ORG_ID,
    employeeID: 'employee-3',
    storeID: MOCK_STORE_ID_2,
    periodStart: '2025-06-09',
    periodEnd: '2025-06-15',
    status: 'Inactive',
    submittedBy: 'employee-3',
    createdAt: '2025-06-16T08:00:00Z',
    updatedAt: '2025-06-16T08:00:00Z',
  },
]

export const mockTimesheetEntries: TimesheetEntry[] = [
  {
    timesheetEntryID: 'timesheet-entry-1',
    timesheetID: 'timesheet-1',
    orgID: MOCK_ORG_ID,
    entryDate: '2025-06-02',
    startTime: '08:00',
    endTime: '16:00',
    minutesWorked: 480,
    activityType: 'Workshop',
    notes: 'Standard shift',
    createdAt: '2025-06-02T16:00:00Z',
    updatedAt: '2025-06-02T16:00:00Z',
  },
  {
    timesheetEntryID: 'timesheet-entry-2',
    timesheetID: 'timesheet-1',
    orgID: MOCK_ORG_ID,
    entryDate: '2025-06-03',
    startTime: '08:00',
    endTime: '15:30',
    minutesWorked: 450,
    activityType: 'Workshop',
    notes: 'Left early',
    createdAt: '2025-06-03T15:30:00Z',
    updatedAt: '2025-06-03T15:30:00Z',
  },
  {
    timesheetEntryID: 'timesheet-entry-3',
    timesheetID: 'timesheet-3',
    orgID: MOCK_ORG_ID,
    entryDate: '2025-06-02',
    startTime: '07:30',
    endTime: '16:00',
    minutesWorked: 510,
    activityType: 'Workshop',
    createdAt: '2025-06-02T16:00:00Z',
    updatedAt: '2025-06-02T16:00:00Z',
  },
  {
    timesheetEntryID: 'timesheet-entry-4',
    timesheetID: 'timesheet-3',
    orgID: MOCK_ORG_ID,
    entryDate: '2025-06-04',
    startTime: '07:30',
    endTime: '16:00',
    minutesWorked: 510,
    activityType: 'Travel',
    travelDistance: 32,
    createdAt: '2025-06-04T16:00:00Z',
    updatedAt: '2025-06-04T16:00:00Z',
  },
  {
    timesheetEntryID: 'timesheet-entry-5',
    timesheetID: 'timesheet-5',
    orgID: MOCK_ORG_ID,
    entryDate: '2025-06-02',
    startTime: '09:00',
    endTime: '17:00',
    minutesWorked: 480,
    activityType: 'Front Desk',
    createdAt: '2025-06-02T17:00:00Z',
    updatedAt: '2025-06-02T17:00:00Z',
  },
]

export const mockTimesheetApprovals: TimesheetApproval[] = [
  {
    timesheetApprovalID: 'timesheet-approval-1',
    orgID: MOCK_ORG_ID,
    timesheetID: 'timesheet-1',
    approverID: MOCK_EMPLOYEE_ID,
    timesheetApprovalsStatus: 'Active',
    createdAt: '2025-06-09T09:00:00Z',
    updatedAt: '2025-06-09T09:00:00Z',
  },
  {
    timesheetApprovalID: 'timesheet-approval-2',
    orgID: MOCK_ORG_ID,
    timesheetID: 'timesheet-3',
    approverID: MOCK_EMPLOYEE_ID,
    timesheetApprovalsStatus: 'Active',
    createdAt: '2025-06-09T09:00:00Z',
    updatedAt: '2025-06-09T09:00:00Z',
  },
  {
    timesheetApprovalID: 'timesheet-approval-3',
    orgID: MOCK_ORG_ID,
    timesheetID: 'timesheet-4',
    approverID: MOCK_EMPLOYEE_ID,
    timesheetApprovalsStatus: 'Terminated',
    rejectionReason: 'Hours mismatch with check-in records',
    createdAt: '2025-06-16T09:00:00Z',
    updatedAt: '2025-06-16T09:00:00Z',
  },
]
